









































































































import { Component, Vue, Prop, Emit, Watch } from 'vue-property-decorator';
import { IRules } from '@/components/UI/Form/ValidationModule';
import { validateMax, validateMin, validateRequired, validateType } from '@/components/UI/Form/Validations';
import { GQL_createInvoiceOverdueNotice, GQL_createInvoiceOverdueNoticeVariables } from '@/queries/__generated__/createInvoiceOverdueNotice';
import { UNIT_INVOICE_SETTINGS } from '@/queries/CompanySetting';
import { SmartQuery } from 'vue-apollo-decorator';
import { GQL_unitInvoiceSetting, GQL_unitInvoiceSetting_currentUnit_unitSetting_unitInvoiceSetting } from '@/queries/__generated__/unitInvoiceSetting';
import { StopActivatingPopup } from '@/decorators/stopPopupActive';
import { GQL_invoice_invoice, GQL_invoice_invoice_invoiceOverdueNotices } from '@/queries/__generated__/invoice';
import { OVERDUE_NOTICE_GRACE_PERIOD_DAYS, OVERDUE_NOTICE_MAX_CHARGE_AMOUNT } from '@/app/Invoice';
import Decimal from '@/utils/decimal';
import gql from 'graphql-tag';

const CREATE_INVOICE_OVERDUE_NOTICE = gql`
  mutation createInvoiceOverdueNotice(
    $invoiceId: ID!,
    $issueDate: Date!,
    $dueDate: Date!,
    $additionalCharge: Decimal!,
  ) {
    createInvoiceOverdueNotice(
      input: {
        invoiceId: $invoiceId,
        issueDate: $issueDate,
        dueDate: $dueDate,
        additionalCharge: $additionalCharge,
      }) {
      invoiceOverdueNotice {
        id
        issueDate
        dueDate
        additionalCharge
        orderNumber
      }
    }
  },
`;

@Component
export default class InvoiceOverdueNoticesForm extends Vue {
  @Prop() private invoice!: GQL_invoice_invoice;
  // Lets callers name the concrete step they are starting, e.g. from the
  // overdue invoice list, instead of the generic "new reminder".
  @Prop({ type: String, default: '' }) private label!: string;
  @Prop({ type: String, default: 'standard' }) private color!: string;
  @SmartQuery<InvoiceOverdueNoticesForm, GQL_unitInvoiceSetting>({
    query: () => UNIT_INVOICE_SETTINGS,
    result({data}) {
      this.overdueNotice.additionalCharge = this.chargableOverdueNotices.length >= 2 ?
        new Decimal(0) : data.currentUnit.unitSetting.unitInvoiceSetting.overdueNoticeCharge!;
    },
  }) private currentUnit!: GQL_unitInvoiceSetting_currentUnit_unitSetting_unitInvoiceSetting;

  private CREATE_INVOICE_OVERDUE_NOTICE = CREATE_INVOICE_OVERDUE_NOTICE;

  private validForm = true;
  private showOverdueNoticeDialog = false;
  private readonly OVERDUE_NOTICE_GRACE_PERIOD_DAYS = OVERDUE_NOTICE_GRACE_PERIOD_DAYS;
  private readonly OVERDUE_NOTICE_MAX_CHARGE_AMOUNT = OVERDUE_NOTICE_MAX_CHARGE_AMOUNT;

  private overdueNotice: GQL_createInvoiceOverdueNoticeVariables = {
    invoiceId: this.invoice.id!,
    issueDate: this.$dayjs().format('YYYY-MM-DD'),
    dueDate: this.$dayjs().add(this.OVERDUE_NOTICE_GRACE_PERIOD_DAYS, 'days').format('YYYY-MM-DD'),
    additionalCharge: new Decimal(0),
  };

  private rules: IRules = {
    issueDate: [
      {
        validation: validateRequired(),
        message: i18n => i18n.t('Error.required', { field: this.t_attr('InvoiceOverdueNotice', 'issueDate') }),
      },
      {
        validation: validateType('date'),
        message: i18n => i18n.t('GQL.Type.date'),
      },
      {
        validation: ({ dataValue }) => {
          if (!dataValue) return false;
          return this.$dayjs.strict(dataValue).isSameOrAfter(this.lastDueDate, 'day');
        },
        message: i18n => i18n.t('Error.mustBeSameOrAfter', {
          beforeField: this.lastDueDate.format('YYYY-MM-DD'),
          afterField: this.t_attr('InvoiceOverdueNotice', 'issueDate'),
        }),
      },
      {
        validation: ({ dataValue }) => {
          if (!dataValue) return false;

          return this.$dayjs.strict(dataValue).isSameOrAfter(this.minimalLawfulIssueDate, 'day');
        },
        message: i18n => {
          const msg = i18n.t('Error.mustBeSameOrAfter', {
            beforeField: this.minimalLawfulIssueDate.format('YYYY-MM-DD'),
            afterField: this.t_attr('InvoiceOverdueNotice', 'issueDate'),
          });
          return `${i18n.t('byLaw')} ${msg}`;
        },
        type: 'warning',
      },
    ],
    dueDate: [
      {
        validation: validateRequired(),
        message: i18n => i18n.t('Error.required', { field: this.t_attr('InvoiceOverdueNotice', 'dueDate') }),
      },
      {
        validation: validateType('date'),
        message: i18n => i18n.t('GQL.Type.date'),
      },
      {
        validation: ({ dataValue }) => {
          if (!dataValue) return false;
          return this.$dayjs.strict(dataValue).isSameOrAfter(this.overdueNotice.issueDate, 'day');
        },
        message: i18n => i18n.t('Error.mustBeSameOrAfter', {
          beforeField: this.t_attr('InvoiceOverdueNotice', 'issueDate'),
          afterField: this.t_attr('InvoiceOverdueNotice', 'dueDate'),
        }),
        triggerBy: [ 'issueDate' ],
      },
      {
        validation: ({ dataValue }) => {
          if (!dataValue) return false;
          if (!this.minimalLawfulDueDate) return false;
          return this.$dayjs.strict(dataValue).isSameOrAfter(this.minimalLawfulDueDate, 'day');
        },
        message: i18n => {
          const msg = i18n.t('Error.mustBeSameOrAfter', {
            beforeField: this.minimalLawfulDueDate ? this.minimalLawfulDueDate.format('YYYY-MM-DD') : this.t_attr('InvoiceOverdueNotice', 'issueDate'),
            afterField: this.t_attr('InvoiceOverdueNotice', 'dueDate'),
          });
          return `${i18n.t('byLaw')} ${msg}`;
        },
        triggerBy: [ 'issueDate' ],
        type: 'warning',
      },
    ],
    additionalCharge: [
      {
        validation: validateMin(0),
        message: i18n => i18n.t('Error.cannotBeNegative', { field: this.t_attr('InvoiceOverdueNotice', 'additionalCharge') }),
      },
      {
        validation: this.chargableOverdueNotices.length < 2 ? validateMax(OVERDUE_NOTICE_MAX_CHARGE_AMOUNT) : validateMax(0),
        message: i18n => {
          const msg = i18n.t('Error.maxAmount', { amount: this.chargableOverdueNotices.length < 2 ? OVERDUE_NOTICE_MAX_CHARGE_AMOUNT : 0 });
          return `${i18n.t('byLaw')} ${msg}`;
        },
        type: 'warning',
      },
    ],
  };

  @StopActivatingPopup()
  private openOverdueNoticeDialog(): void {
    this.showOverdueNoticeDialog = true;
  }

  @Emit()
  private mutationDone(data: {data: GQL_createInvoiceOverdueNotice }) {
    if (!data.data.createInvoiceOverdueNotice?.invoiceOverdueNotice) return;
    this.showOverdueNoticeDialog = false;
    this.$set(this.invoice, 'invoiceOverdueNotices', this.invoice.invoiceOverdueNotices.concat(data.data.createInvoiceOverdueNotice.invoiceOverdueNotice));
    this.overdueNotice.issueDate = this.$dayjs().format('YYYY-MM-DD');
    this.overdueNotice.dueDate = this.$dayjs().add(this.OVERDUE_NOTICE_GRACE_PERIOD_DAYS, 'days').format('YYYY-MM-DD');
    if (this.chargableOverdueNotices.length >= 2) this.overdueNotice.additionalCharge = new Decimal(0);
  }

  private get lastOverdueNotice(): GQL_invoice_invoice_invoiceOverdueNotices | undefined {
    return this.invoice.invoiceOverdueNotices[this.invoice.invoiceOverdueNotices.length - 1];
  }

  private get lastDueDate() {
    const date = this.lastOverdueNotice ? this.lastOverdueNotice.dueDate : this.invoice.dueDate;
    return this.$dayjs.strict(date);
  }

  private get chargableOverdueNotices(): GQL_invoice_invoice_invoiceOverdueNotices[] {
    return this.invoice.invoiceOverdueNotices.filter(notice => [ 1, 2 ].includes(notice.orderNumber));
  }

  private get minimalLawfulIssueDate() {
    return this.lastDueDate.add(this.OVERDUE_NOTICE_GRACE_PERIOD_DAYS, 'days');
  }

  private get minimalLawfulDueDate() {
    const issueDate = this.$dayjs.strict(this.overdueNotice.issueDate);
    return issueDate.isValid() ? issueDate.add(this.OVERDUE_NOTICE_GRACE_PERIOD_DAYS, 'days') : null;
  }

  @Watch('overdueNotice.issueDate')
  private updateDueDate(): void {
    const issueDate = this.$dayjs.strict(this.overdueNotice.issueDate);
    this.overdueNotice.dueDate = issueDate.isValid() ? issueDate
      .add(this.OVERDUE_NOTICE_GRACE_PERIOD_DAYS, 'days')
      .format('YYYY-MM-DD') : null;
  }
}
